import {
  createContext,
  useContext,
  useCallback,
  useState,
  type ComponentProps,
  type MouseEvent,
  type KeyboardEvent,
} from "react";
import { ExpandedImageDialog } from "./ExpandedImageDialog";
import type { ExpandedImagePreview } from "./ExpandedImagePreview";
import { markdownImageGallery, markdownImageItems } from "./markdownImageGallery";
import { cn } from "../../lib/utils";

export const MarkdownImageLinkContext = createContext(false);

/** Image URLs have already passed the Markdown renderer's URL policy. */
export function MarkdownImage({ src, alt = "", className, ...props }: ComponentProps<"img">) {
  const isLinked = useContext(MarkdownImageLinkContext);
  const [preview, setPreview] = useState<ExpandedImagePreview | null>(null);
  const [loadState, setLoadState] = useState({ src, failed: false });
  if (loadState.src !== src) setLoadState({ src, failed: false });
  const failed = loadState.src === src && loadState.failed;
  const register = useCallback(
    (image: HTMLImageElement | null) => {
      if (image && typeof src === "string" && src && !failed) {
        markdownImageItems.set(image, { src, name: alt.trim() || "image" });
        return () => {
          markdownImageItems.delete(image);
        };
      }
    },
    [src, alt, failed],
  );
  const open = (event: MouseEvent<HTMLImageElement> | KeyboardEvent<HTMLImageElement>) => {
    if (event.currentTarget.closest("a")) return;
    const item = markdownImageItems.get(event.currentTarget);
    if (!item) return;
    event.preventDefault();
    event.stopPropagation();
    setPreview(markdownImageGallery(event.currentTarget, item));
  };
  return (
    <>
      <img
        {...props}
        ref={register}
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        className={cn("max-w-full rounded-lg", !isLinked && "cursor-zoom-in", className)}
        role={isLinked ? undefined : "button"}
        tabIndex={isLinked ? undefined : 0}
        aria-label={isLinked ? undefined : `Expand ${alt.trim() || "image"}`}
        onClick={isLinked ? undefined : open}
        onKeyDown={
          isLinked
            ? undefined
            : (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  if (event.key === " ") event.preventDefault();
                  open(event);
                }
              }
        }
        onError={(event) => {
          markdownImageItems.delete(event.currentTarget);
          setLoadState({ src, failed: true });
        }}
      />
      {preview ? <ExpandedImageDialog preview={preview} onClose={() => setPreview(null)} /> : null}
    </>
  );
}
